import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  FaMoneyBillWave,
  FaCreditCard,
  FaChartLine,
  FaPlus,
  FaTimes,
  FaTrash,
  FaEdit,
  FaRupeeSign,
  FaCalendarAlt,
  FaUser,
  FaArrowUp,
  FaArrowDown,
  FaPhone,
  FaStar,
  FaShieldAlt,
  FaHeadset,
  FaEye,
  FaFilter,
  FaAd,
  FaHeart,
  FaBrain,
  FaStore,
} from "react-icons/fa";

import api from "../../services/api";
import CustomDropdown from "./CustomDropdown";
import { calculateFinalPrice } from "../../utils/pricingCalculator";
import { formatCurrency } from "../../utils/currency";

const RevenueManagement = () => {
  const [stats, setStats] = useState({
    totalRevenue: 0,
    activeSubscriptions: 0,
    avgRevenue: 0,
    transactions: [],
  });
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [timeRange, setTimeRange] = useState("monthly");
  const [message, setMessage] = useState({ text: "", type: "", show: false });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const initialPlanState = {
    name: "",
    displayName: "",
    price: { monthly: "", quarterly: "", yearly: "" },
    offerPrice: { monthly: "", quarterly: "", yearly: "" },
    taxApplicable: true,
    features: {
      profileViews: -1,
      profileViewsPerDay: -1,
      contactViewQuota: 10,
      verifiedBadge: false,
      hideAds: false,
      horoscopeMatching: false,
      jathagam: false,
      aiJathagam: false,
      marketAccess: false,
      chatAccess: false,
      advancedSearch: false,
    },
    badge: { text: "", color: "blue", icon: "FaStar" },
    popular: false,
    active: true,
    profileViewAccess: {} // dynamic toggles

  };

  const [newPlan, setNewPlan] = useState(initialPlanState);

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage]);

  const showMessage = (text, type) => {
    setMessage({ text, type, show: true });
    setTimeout(() => {
      setMessage({ text: "", type: "", show: false });
    }, 3000);
  };

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const [revenueRes, plansRes] = await Promise.all([
        api.get(`/admin/revenue?page=${page}&limit=${itemsPerPage}`),
        api.get("/admin/plans"),
      ]);
      setStats(revenueRes.data);
      setPlans(plansRes.data);
    } catch (error) {
      console.error("Failed to fetch revenue data", error);
      showMessage("Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFeatureChange = (feature, value) => {
    setNewPlan((prev) => ({
      ...prev,
      features: { ...prev.features, [feature]: value },
    }));
  };

  const handlePriceChange = (interval, value) => {
    setNewPlan((prev) => ({
      ...prev,
      price: { ...prev.price, [interval]: value },
    }));
  };

  const handleOpenEditModal = (plan) => {
    const availability = {};

    plans.forEach((p) => {
      availability[p.name] =
        plan.profileViewAccess?.[p.name] ?? false; // default OFF
    });

    setNewPlan({
      name: plan.name,
      displayName: plan.displayName,
      price: {
        monthly: plan.price.monthly,
        quarterly: plan.price.quarterly || "",
        yearly: plan.price.yearly || "",
      },
      offerPrice: {
        monthly: plan.offerPrice?.monthly || "",
        quarterly: plan.offerPrice?.quarterly || "",
        yearly: plan.offerPrice?.yearly || "",
      },
      features: { ...initialPlanState.features, ...plan.features },
      badge: { ...initialPlanState.badge, ...plan.badge },
      popular: plan.popular || false,
      active: plan.active !== false,
      taxType: plan.taxType || (plan.taxApplicable === false ? "none" : "exclusive"),
      taxApplicable:
        (plan.taxType || (plan.taxApplicable === false ? "none" : "exclusive")) !== "none",

      // ✅ correct
      profileViewAccess: availability,
    });

    setEditingPlanId(plan._id);
    setIsEditMode(true);
    setShowAddModal(true);
  };


  const handleOpenAddModal = () => {
    const availability = {};

    plans.forEach((p) => {
      availability[p.name] = false; // default ON
    });

    setNewPlan({
      ...initialPlanState,
      profileViewAccess: availability
    });

    setIsEditMode(false);
    setEditingPlanId(null);
    setShowAddModal(true);
  };


  const handleDeletePlan = async (planId) => {
    try {
      await api.delete(`/admin/plans/${planId}`);
      showMessage("Plan deleted successfully", "success");
      fetchData();
    } catch (error) {
      showMessage("Failed to delete plan", "error");
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!newPlan.displayName || !newPlan.name) {
        showMessage("Plan Name is required", "error");
        return;
      }

      const payload = {
        ...newPlan,
        price: {
          monthly: Number(newPlan.price.monthly) || 0,
          quarterly: Number(newPlan.price.quarterly) || 0,
          yearly: Number(newPlan.price.yearly) || 0,
        },
      };

      if (isEditMode) {
        await api.put(`/admin/plans/${editingPlanId}`, payload);
        showMessage("Plan updated successfully", "success");
      } else {
        await api.post("/admin/plans", payload);
        showMessage("Plan added successfully", "success");
      }

      setShowAddModal(false);
      setNewPlan(initialPlanState);
      setIsEditMode(false);
      setEditingPlanId(null);
      fetchData();
    } catch (error) {
      console.error(error);
      showMessage(
        error.response?.data?.message || "Failed to save plan",
        "error"
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mb-4"></div>
          <p className="text-gray-600 text-lg">Loading revenue data...</p>
        </div>
      </div>
    );
  }

  const totalTransactions = stats.pagination?.total || 0;
  const totalPages = stats.pagination?.totalPages || 1;
  const currentTransactions = stats.transactions || [];

  const indexOfFirstItem = (currentPage - 1) * itemsPerPage;
  const indexOfLastItem = Math.min(currentPage * itemsPerPage, totalTransactions);

  return (
    <div className="space-y-8">
      {/* Message Popup */}
      {message.show && (
        <div
          className={`fixed top-4 right-4 z-50 transform transition-all duration-300 ${message.type === "success"
            ? "bg-blue-500"
            : message.type === "error"
              ? "bg-gray-400"
              : "bg-blue-600"
            } text-white px-6 py-3 rounded-xl shadow-2xl flex items-center space-x-3 animate-slide-in`}
        >
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center ${message.type === "success"
              ? "bg-blue-600"
              : message.type === "error"
                ? "bg-gray-500"
                : "bg-blue-700"
              }`}
          >
            {message.type === "success"
              ? "✓"
              : message.type === "error"
                ? "✗"
                : "ⓘ"}
          </div>
          <span className="font-medium">{message.text}</span>
          <button
            onClick={() => setMessage({ ...message, show: false })}
            className="ml-4 hover:opacity-80"
          >
            <FaTimes />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-200 relative">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              <FaMoneyBillWave className="mr-3 text-blue-500" />
              Revenue Management
            </h1>
            <p className="text-gray-600 mt-1">
              Track revenue, manage subscriptions, and configure plans
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={fetchData}
              className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors duration-200 flex items-center"
            >
              <FaFilter className="mr-2" /> Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Revenue Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-2xl shadow-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium mb-1">
                Total Revenue
              </p>
              <h3 className="text-3xl font-bold mb-2">
                ₹ {(stats.totalRevenue || 0).toLocaleString()}
              </h3>
              <div className="flex items-center text-sm text-blue-200">
                <FaArrowUp className="mr-1" /> 12.5% from last month
              </div>
            </div>
            <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <FaMoneyBillWave className="text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-2xl shadow-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium mb-1">
                Active Subscriptions
              </p>
              <h3 className="text-3xl font-bold mb-2">
                {stats.activeSubscriptions || 0}
              </h3>
              <div className="flex items-center text-sm text-green-200">
                <FaArrowUp className="mr-1" /> 8 new this week
              </div>
            </div>
            <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <FaCreditCard className="text-2xl" />
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-2xl shadow-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium mb-1">
                Avg Revenue / User
              </p>
              <h3 className="text-3xl font-bold mb-2">
                ₹ {(stats.avgRevenue || 0).toLocaleString()}
              </h3>
              <div className="flex items-center text-sm text-purple-200">
                <FaArrowDown className="mr-1" /> 2.3% from last month
              </div>
            </div>
            <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <FaChartLine className="text-2xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="px-8 py-6 border-b border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-bold text-gray-800">
                Recent Transactions
              </h3>
              <p className="text-gray-600 text-sm mt-1">
                Latest payment activities
              </p>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider text-left">
                  Transaction ID
                </th>
                <th className="px-6 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider text-left">
                  User
                </th>
                <th className="px-6 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider text-left">
                  User Status
                </th>
                <th className="px-6 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider text-left">
                  Plan
                </th>
                <th className="px-6 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider text-left">
                  Billing Cycle
                </th>
                <th className="px-6 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider text-left">
                  Payment Date
                </th>
                <th className="px-6 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider text-left">
                  Start Date
                </th>
                <th className="px-6 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider text-left">
                  Expiry Date
                </th>
                <th className="px-6 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider text-left">
                  Amount
                </th>
                <th className="px-6 py-5 font-semibold text-gray-600 text-sm uppercase tracking-wider text-left">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {currentTransactions.length > 0 ? (
                currentTransactions.map((tx, i) => (
                  <tr
                    key={i}
                    className="hover:bg-gray-50/80 transition-colors duration-150"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-800">
                      {tx.transactionId || "N/A"}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-800 text-sm whitespace-nowrap">
                          {tx.userId?.name || "Unknown User"}
                        </span>
                        {tx.userId?.mobile && (
                          <span className="text-gray-500 text-xs mt-0.5 whitespace-nowrap">
                            {tx.userId.mobile}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-3 py-1.5 rounded-full text-xs font-medium ${tx.userId?.status === "inactive"
                            ? "bg-red-100 text-red-700"
                            : tx.userId?.isBlocked
                              ? "bg-yellow-100 text-yellow-700"
                              : "bg-green-100 text-green-700"
                          }`}
                      >
                        {tx.userId?.status === "inactive"
                          ? "Deleted"
                          : tx.userId?.isBlocked
                            ? "Blocked"
                            : "Active"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-xs font-medium capitalize">
                        {tx.plan || "N/A"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium capitalize">
                        {tx.billingCycle || "N/A"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {tx.paymentDate ? new Date(tx.paymentDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-') : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {tx.startDate ? new Date(tx.startDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-') : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {tx.expiryDate ? new Date(tx.expiryDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-') : 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm font-bold text-gray-800">
                      ₹{formatCurrency(tx.amount)}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-3 py-1.5 rounded-full text-xs font-medium ${tx.status === "success"
                          ? "bg-green-100 text-green-700"
                          : tx.status === "failed"
                            ? "bg-red-100 text-red-700"
                            : "bg-yellow-100 text-yellow-700"
                          }`}
                      >
                        {tx.status ? tx.status.charAt(0).toUpperCase() + tx.status.slice(1) : "N/A"}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center py-16">
                    <div className="flex flex-col items-center">
                      <FaMoneyBillWave className="text-6xl text-gray-300 mb-4" />
                      <p className="text-gray-600 text-lg font-medium">
                        No transactions found
                      </p>
                      <p className="text-gray-500 mt-1">
                        Payment activities will appear here
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="px-8 py-5 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
            <span className="text-sm text-gray-600">
              Showing <span className="font-semibold text-gray-900">{indexOfFirstItem + 1}</span> to{" "}
              <span className="font-semibold text-gray-900">
                {indexOfLastItem}
              </span>{" "}
              of <span className="font-semibold text-gray-900">{totalTransactions}</span> entries
            </span>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Previous
              </button>

              {/* Page Numbers */}
              <div className="flex space-x-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                  // Show max 5 pages, with current in middle if possible
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${currentPage === page
                            ? "bg-blue-600 text-white shadow-md shadow-blue-500/30"
                            : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                          }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === currentPage - 2 ||
                    page === currentPage + 2
                  ) {
                    return <span key={page} className="px-2 py-2 text-gray-400">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Subscription Plans */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <div>
            <h3 className="text-xl font-bold text-gray-800">
              Subscription Plans
            </h3>
            <p className="text-gray-600 mt-1">
              Manage and configure subscription packages
            </p>
          </div>
          <button
            onClick={handleOpenAddModal}
            className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl font-medium shadow-lg shadow-blue-500/30 transition-all duration-200 flex items-center"
          >
            <FaPlus className="mr-2" /> Create New Plan
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan, index) => {
            const CARD_THEMES = [
                { header: 'bg-[#FFC61A]', text: 'text-[#1A1A1A]', strikethrough: 'text-[#4D4D4D]' },
                { header: 'bg-[#A754D7]', text: 'text-white', strikethrough: 'text-purple-200' },
                { header: 'bg-[#DE4141]', text: 'text-white', strikethrough: 'text-red-200' },
                { header: 'bg-[#43B05C]', text: 'text-white', strikethrough: 'text-green-200' },
            ];
            const theme = CARD_THEMES[index % CARD_THEMES.length];
            const price = plan.price?.monthly || 0;
            const discount = plan.offerPrice?.monthly || 0;
            const finalPrice = discount > 0 && discount < price ? price - discount : price;

            return (
            <div
              key={plan._id}
              className={`relative rounded-3xl bg-white shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-200 flex flex-col overflow-hidden group ${plan.popular ? 'scale-105 border-2 border-yellow-400' : ''}`}
            >
              {/* Action Buttons */}
              <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity z-20">
                <button
                  onClick={() => handleOpenEditModal(plan)}
                  className="p-2.5 bg-white hover:bg-gray-100 text-blue-600 rounded-xl shadow-lg transition-colors duration-200"
                  title="Edit Plan"
                >
                  <FaEdit />
                </button>
                <button
                  onClick={() => handleDeletePlan(plan._id)}
                  className="p-2.5 bg-white hover:bg-red-50 text-red-600 rounded-xl shadow-lg transition-colors duration-200"
                  title="Delete Plan"
                >
                  <FaTrash />
                </button>
              </div>

              {/* Colorful Header */}
              <div className={`relative pt-10 pb-8 px-6 flex flex-col items-center justify-center ${theme.header}`} style={{ borderRadius: '0 0 50% 50% / 0 0 25px 25px' }}>
                  {discount > 0 && discount < price && (
                      <div 
                          className="absolute top-4 left-0 bg-[#111] text-white px-5 py-1.5 font-black text-[12px] tracking-wider uppercase z-10 shadow-md"
                          style={{ clipPath: 'polygon(0% 0%, 100% 0%, 85% 50%, 100% 100%, 0% 100%)' }}
                      >
                          OFFER
                      </div>
                  )}

                  {plan.popular && (
                      <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-[10px] font-bold shadow-sm uppercase tracking-wide whitespace-nowrap">
                          ⭐ Popular
                      </div>
                  )}

                  <h3 className={`text-xl font-bold mb-4 uppercase tracking-widest ${theme.text}`}>
                      {plan.displayName}
                  </h3>
                  
                  <div className="flex w-full justify-between items-start px-1 mb-2">
                    {[
                      { label: 'Monthly', p: plan.price?.monthly, d: plan.offerPrice?.monthly },
                      { label: 'Quarterly', p: plan.price?.quarterly, d: plan.offerPrice?.quarterly },
                      { label: 'Yearly', p: plan.price?.yearly, d: plan.offerPrice?.yearly }
                    ].map(tier => {
                      const pricing = calculateFinalPrice(tier.p, tier.d, plan.taxType, plan.taxApplicable);
                      if (!pricing) return null;
                      
                      return (
                          <div key={tier.label} className="flex flex-col items-center flex-1">
                              <span className={`text-[10px] uppercase tracking-wider font-bold mb-1 opacity-80 ${theme.text}`}>{tier.label}</span>
                              
                              {pricing.discountedPrice < pricing.basePrice ? (
                                  <>
                                      <div className={`text-3xl font-black tracking-tighter ${theme.text} leading-none`}>
                                          ₹{formatCurrency(pricing.discountedPrice)}
                                      </div>
                                      <div className="flex flex-wrap items-center justify-center gap-1 mt-1">
                                          <span className={`text-[11px] line-through font-semibold ${theme.strikethrough}`}>
                                            ₹{formatCurrency(pricing.basePrice)}
                                          </span>
                                          <span className={`text-[12px] font-black italic ${theme.text} whitespace-nowrap`}>
                                              {tier.d}% off
                                          </span>
                                      </div>
                                  </>
                              ) : (
                                  <div className={`text-[38px] md:text-[42px] font-black tracking-tighter ${theme.text} leading-none whitespace-nowrap`}>
                                      ₹{formatCurrency(pricing.basePrice)}
                                  </div>
                              )}
                              
                              {/* GST + Final Amount */}
                              <div className={`mt-3 mb-1 flex flex-col items-center`}>
                                  {pricing.taxType !== 'none' && (
                                    <span className={`text-[10px] font-semibold tracking-wide ${theme.text} opacity-70`}>
                                        {pricing.gstText}
                                    </span>
                                  )}
                                  <span className={`text-[11px] font-bold mt-1 ${theme.text} text-center leading-tight`}>
                                    Final Amount:
                                  </span>
                                  <span className={`text-[11px] font-bold ${theme.text} whitespace-nowrap`}>
                                    ₹{formatCurrency(pricing.finalTotal)}
                                  </span>
                              </div>
                          </div>
                      );
                    })}
                  </div>
              </div>

              {/* Features List */}
              <div className="p-8 flex-1 bg-white space-y-3 mb-8">
                <FeatureItem
                  icon={<FaEye />}
                  text={`${plan.features?.contactViewQuota || 0} Contact Views`}
                  included={true}
                />
                <FeatureItem
                  icon={<FaEye />}
                  text={plan.features?.profileViews === -1 ? "Unlimited Profile Views" : `${plan.features?.profileViews} Profile Views`}
                  included={plan.features?.profileViews > 0 || plan.features?.profileViews === -1}
                />
                <FeatureItem
                  icon={<FaAd />}
                  text="No Ads"
                  included={plan.features?.hideAds}
                />
                <FeatureItem
                  icon={<FaHeart />}
                  text="Horoscope Matching"
                  included={plan.features?.horoscopeMatching}
                />
                <FeatureItem
                  icon={<FaBrain />}
                  text="Jathagam Analysis"
                  included={plan.features?.jathagam}
                />
                {/* <FeatureItem
                  icon={<FaStore />}
                  text="Market Access"
                  included={plan.features?.marketAccess}
                /> */}
                <FeatureItem
                  icon={<FaStore />}
                  text="Chat Access"
                  included={plan.features?.chatAccess}
                />
                <FeatureItem
                  icon={<FaFilter />}   // better icon for search
                  text="Advanced Search"
                  included={plan.features?.advancedSearch}
                />

                <FeatureItem
                  icon={<FaShieldAlt />}
                  text="Verified Badge"
                  included={plan.features?.verifiedBadge}
                />


              </div>
            </div>
          )})}
        </div>
      </div>

      {/* Create/Edit Plan Modal */}
      {showAddModal && createPortal(
        <div className="fixed inset-0 bg-black/60 z-[9999] flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header - Now Sticky */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-6 rounded-t-2xl z-40">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    {isEditMode ? "Edit Plan" : "Create New Plan"}
                  </h2>
                  <p className="text-gray-600 mt-1">
                    Configure subscription package details
                  </p>
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="p-2.5 hover:bg-gray-100 rounded-xl transition-colors duration-200"
                >
                  <FaTimes className="text-gray-500 text-xl" />
                </button>
              </div>
            </div>

            <form onSubmit={handleFormSubmit} className="px-8 py-6 space-y-8">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Display Name
                  </label>
                  <input
                    required
                    type="text"
                    placeholder="e.g. Diamond Plus"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    value={newPlan.displayName}
                    onChange={(e) =>
                      setNewPlan({ ...newPlan, displayName: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Internal ID
                  </label>
                  <input
                    required
                    type="text"
                    placeholder="e.g. diamond_plus"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 lowercase"
                    value={newPlan.name}
                    onChange={(e) =>
                      setNewPlan({
                        ...newPlan,
                        name: e.target.value.toLowerCase().replace(/\s+/g, "_"),
                      })
                    }
                    disabled={isEditMode}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Tax Applicable
                  </label>
                    <CustomDropdown
                      options={[
                        { value: "exclusive", label: "Exclusive (+18% GST Extra)" },
                        { value: "inclusive", label: "Inclusive (18% GST Included)" },
                        { value: "none", label: "None (No Tax)" }
                      ]}
                      value={newPlan.taxType || (newPlan.taxApplicable === false ? "none" : "exclusive")}
                      onChange={(val) =>
                        setNewPlan({ 
                          ...newPlan, 
                          taxType: val,
                          taxApplicable: val !== "none"
                        })
                      }
                      placeholder="Select Tax"
                      searchable={false}
                    />
                </div>
              </div>

              {/* Pricing Section */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
                  <FaRupeeSign className="mr-2 text-blue-500" /> Pricing
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { key: "monthly", label: "Monthly", required: true },
                    { key: "quarterly", label: "Quarterly", required: false },
                    { key: "yearly", label: "Yearly", required: false },
                  ].map(({ key, label, required }) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {label}{" "}
                        {required && <span className="text-red-500">*</span>}
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                          ₹
                        </span>
                        <input
                          type="text"
                          inputMode="numeric"
                          required={required}
                          className="w-full pl-8 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          value={newPlan.price[key]}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            handlePriceChange(key, val);
                          }}
                          placeholder="0"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Offer Pricing Section */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-semibold text-gray-800 mb-4 flex items-center">
                  <FaRupeeSign className="mr-2 text-green-500" /> Discount Percentage (Optional)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {[
                    { key: "monthly", label: "Monthly Discount (%)" },
                    { key: "quarterly", label: "Quarterly Discount (%)" },
                    { key: "yearly", label: "Yearly Discount (%)" },
                  ].map(({ key, label }) => (
                    <div key={`offer_${key}`}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {label}
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">
                          %
                        </span>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="w-full pl-8 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                          value={newPlan.offerPrice?.[key] || ""}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            setNewPlan({
                              ...newPlan,
                              offerPrice: {
                                ...newPlan.offerPrice,
                                [key]: val
                              }
                            });
                          }}
                          placeholder="0"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Features Section */}
              <div>
                <h4 className="font-semibold text-gray-800 mb-4">
                  Features & Limits
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Quotas */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contact View Quota
                      </label>
                      <input
                        type="number"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={newPlan.features.contactViewQuota}
                        onChange={(e) =>
                          handleFeatureChange("contactViewQuota", e.target.value)
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Profile Views (-1 for unlimited)
                      </label>
                      <input
                        type="number"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={newPlan.features.profileViews}
                        onChange={(e) =>
                          handleFeatureChange("profileViews", e.target.value)
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Profile Views Per Day (-1 for unlimited)
                      </label>
                      <input
                        type="number"
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        value={newPlan.features.profileViewsPerDay}
                        onChange={(e) =>
                          handleFeatureChange("profileViewsPerDay", e.target.value)
                        }
                      />
                    </div>
                  </div>

                  {/* Toggles */}
                  <div className="space-y-3">
                    <ToggleSwitch
                      label="Hide Ads"
                      checked={newPlan.features.hideAds}
                      onChange={(v) => handleFeatureChange("hideAds", v)}
                    />
                    <ToggleSwitch
                      label="Horoscope Matching"
                      checked={newPlan.features.horoscopeMatching}
                      onChange={(v) => handleFeatureChange("horoscopeMatching", v)}
                    />
                    {/* <ToggleSwitch
                      label="AI Jathagam"
                      checked={newPlan.features.aiJathagam}
                      onChange={(v) => handleFeatureChange("aiJathagam", v)}
                    /> */}
                    <ToggleSwitch
                      label="View Jathagam"
                      checked={newPlan.features.jathagam}
                      onChange={(v) => handleFeatureChange("jathagam", v)}
                    />
                    {/* <ToggleSwitch
                      label="Market Access"
                      checked={newPlan.features.marketAccess}
                      onChange={(v) => handleFeatureChange("marketAccess", v)}
                    /> */}
                    <ToggleSwitch
                      label="Chat Access"
                      checked={newPlan.features.chatAccess}
                      onChange={(v) => handleFeatureChange("chatAccess", v)}
                    />
                    <ToggleSwitch
                      label="Advanced Search"
                      checked={newPlan.features.advancedSearch}
                      onChange={(v) => handleFeatureChange("advancedSearch", v)}
                    />

                    <ToggleSwitch
                      label="Verified Badge"
                      checked={newPlan.features.verifiedBadge}
                      onChange={(v) => handleFeatureChange("verifiedBadge", v)}
                    />
                    <ToggleSwitch
                      label="Popular Plan"
                      checked={newPlan.popular}
                      onChange={(v) => setNewPlan({ ...newPlan, popular: v })}
                    />

                    <ToggleSwitch
                      label="Active"
                      checked={newPlan.active}
                      onChange={(v) => setNewPlan({ ...newPlan, active: v })}
                    />
                  </div>
                </div>
              </div>
              {/* Available For Plans */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h4 className="font-semibold text-gray-800 mb-4">
                  Profile Shows
                </h4>
                <div className="space-y-3">
                  {plans.map((p) => (
                    <ToggleSwitch
                      key={p._id}
                      label={`${p.displayName} Shows`}
                      checked={newPlan.profileViewAccess?.[p.name] ?? true}
                      onChange={(value) =>
                        setNewPlan((prev) => ({
                          ...prev,
                          profileViewAccess: {
                            ...prev.profileViewAccess,
                            [p.name]: value,
                          },
                        }))
                      }
                    />
                  ))}
                </div>
              </div>


              {/* Action Buttons - Now Sticky Footer */}
              <div className="sticky bottom-0 bg-white border-t border-gray-200 flex justify-end space-x-3 pt-6 pb-6 px-8 -mx-8">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white rounded-xl font-medium shadow-lg shadow-blue-500/30 transition-all duration-200"
                >
                  {isEditMode ? "Update Plan" : "Create Plan"}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

const FeatureItem = ({ icon, text, included = true }) => (
  <div className="flex items-center">
    <div
      className={`w-6 h-6 rounded-lg flex items-center justify-center mr-3 ${included ? "bg-blue-100 text-blue-600" : "bg-gray-100 text-gray-400"
        }`}
    >
      {included ? icon : <FaTimes />}
    </div>
    <span
      className={`text-sm ${included ? "text-gray-700" : "text-gray-400 line-through"
        }`}
    >
      {text}
    </span>
  </div>
);

const ToggleSwitch = ({ label, checked, onChange }) => (
  <div className="flex items-center justify-between py-2">
    <span className="text-sm font-medium text-gray-700">{label}</span>
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${checked ? "bg-blue-500" : "bg-gray-300"
        }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-1"
          }`}
      />
    </button>
  </div>
);

export default RevenueManagement;